/**
 * `button` → **Button column settings** — rebuilt from the capture
 * (`discovery/ui-reference/editors/button/editor.{png,json}`, the dialog the cell itself opens).
 *
 * Measured: a **400 × 472** card; the title *Button column settings* (368 × 24 at the 16 px
 * inset); and, at the bottom, an illustration block — the heading *Button column* (106 × 24), the
 * paragraph *"Set up automatic actions like duplicate, move item, notify someone and more…"*
 * (352 × 42) and a 79 × 40 **Set up** button.
 *
 * The label and colour controls sit in the 278 px the capture records as an image region, so
 * their widgets were not recorded — but their **store** is documented: `buttonText`
 * (*"defaults to `Click me`"*) and `color` (`#579bfc` / `#037f4c` observed on this account)
 * `[09§A.7 button]`, `[cvs§2 button]`. They are rendered as the kit's text field and colour row,
 * which is the conservative reading; the exact widgets are q525.
 *
 * **Set up** opens monday's automation builder. This product's automations centre is a different
 * screen and the button's trigger (`button_clicked`, block 963) is configured there, so the block
 * is rendered as monday's copy with the action disabled rather than wired to the wrong place —
 * "absent, not a lie" (B1 §3.2).
 */
import { BUTTON_DEFAULT_COLOR, BUTTON_DEFAULT_LABEL, type ButtonSettings } from '@elizmonday/shared';
import {
  ColorSwatches,
  SETTINGS_COLOR_NAMES,
  HideSummaryField,
  SettingsDivider,
  SettingsField,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

export function ButtonSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<ButtonSettings>) {
  const [draft, patch] = useSettingsDraft<ButtonSettings>(settings);
  return (
    <SettingsForm
      title="Button column settings"
      testId="button-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsField
        label="Button label"
        testId="button-label"
        value={draft.buttonText ?? BUTTON_DEFAULT_LABEL}
        placeholder={BUTTON_DEFAULT_LABEL}
        maxLength={40}
        onChange={(next) => patch({ buttonText: next })}
      />
      {/*
        The row leads with the two colours this account's own button columns actually wear —
        `#579bfc` (bright_blue, monday's default) and `#037f4c` (grass_green) `[cvs§2 button]` —
        so a column imported from monday shows its stored colour as the picked swatch rather than
        as nothing at all.
      */}
      <ColorSwatches
        testId="button-color"
        value={draft.color ?? BUTTON_DEFAULT_COLOR}
        names={['bright_blue', 'grass_green', ...SETTINGS_COLOR_NAMES.filter((name) => name !== 'grass_green')]}
        onChange={(hex) => patch({ color: hex })}
      />

      <SettingsDivider />
      <p className="ez-type-settings__section">Button column</p>
      <p className="ez-type-settings__hint" data-testid="button-automation-lede">
        Set up automatic actions like duplicate, move item, notify someone and more…
      </p>
      <p className="ez-type-settings__hint">
        A press fires the <code>button_clicked</code> trigger; the action it runs is configured in
        this board&rsquo;s automations.
      </p>

      <HideSummaryField
        testId="button-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
